import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, ChevronDown, Globe, Loader2, Lock, Mail, MapPin, Send } from "lucide-react";
import Reveal from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initial = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  country: "",
  website: "",
  locations: "",
  interest: "general",
  message: "",
  consent: false,
  fax: "",
};

function Field({ id, label, required, optional, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
        {required && <span className="ml-1 text-teal-700" aria-hidden="true">*</span>}
        {optional && <span className="ml-1 font-normal text-navy-900/45">{optional}</span>}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="error-text" role="alert" data-testid={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

function SelectWrap({ children, testid }) {
  return (
    <div className="relative">
      {children}
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-900/50" aria-hidden="true" data-testid={testid} />
    </div>
  );
}

export default function Contact() {
  const { lang, t } = useLang();
  const f = t.contact.form;
  const base = `/${lang}`;
  const [params] = useSearchParams();
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    const interest = params.get("interest");
    if (interest && f.interestOptions.some((o) => o.v === interest)) {
      setValues((v) => ({ ...v, interest }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (values.name.trim().length < 2) er.name = f.errors.name;
    if (values.organization.trim().length < 2) er.organization = f.errors.organization;
    if (!EMAIL_RE.test(values.email.trim())) er.email = f.errors.email;
    if (!values.country) er.country = f.errors.country;
    if (values.message.trim().length < 20) er.message = f.errors.message;
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) return;

    setStatus("sending");
    try {
      const payload = {
        name: values.name.trim(),
        organization: values.organization.trim(),
        email: values.email.trim(),
        phone: values.phone.trim() || null,
        country: values.country,
        website: values.website.trim() || null,
        locations: values.locations || null,
        interest: values.interest || null,
        message: values.message.trim(),
        marketing_consent: values.consent,
        fax: values.fax,
      };
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, payload);
      setStatus("success");
      toast.success(f.success.title);
    } catch (err) {
      const message = err.response && err.response.status === 429 ? f.errorRate : f.errorGeneral;
      setServerError(message);
      setStatus("error");
      toast.error(message);
    }
  };

  const reset = () => {
    setValues(initial);
    setErrors({});
    setStatus("idle");
  };

  return (
    <>
      <Seo title={t.meta.contact.title} description={t.meta.contact.description} />

      <section className="bg-paper-warm">
        <div className="mx-auto max-w-7xl px-5 pb-24 pt-14 sm:px-8 md:pt-20">
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="eyebrow flex items-center gap-3">
                  <span className="h-px w-8 bg-teal-600" aria-hidden="true" />
                  {t.contact.hero.eyebrow}
                </p>
                <h1 className="mt-6 font-display text-4xl leading-[1.08] text-navy-900 sm:text-5xl" data-testid="contact-title">
                  {t.contact.hero.title}
                </h1>
                <p className="mt-6 text-lg leading-relaxed text-navy-900/75">{t.contact.hero.sub}</p>
              </Reveal>

              <Reveal delay={0.1}>
                <dl className="mt-10 space-y-6 border-t border-navy-900/10 pt-8" data-testid="contact-info">
                  <div>
                    <dt className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-navy-900/50">{t.contact.info.emailLabel}</dt>
                    <dd className="mt-1.5">
                      <a href="mailto:info@tonouhmarketing.com" data-testid="contact-email-link" className="inline-flex items-center gap-2 font-semibold text-teal-700 underline underline-offset-4 transition-colors duration-200 hover:text-teal-800">
                        <Mail className="h-4 w-4" />
                        info@tonouhmarketing.com
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-navy-900/50">{t.contact.info.locationLabel}</dt>
                    <dd className="mt-1.5 flex items-center gap-2 font-semibold text-navy-900">
                      <MapPin className="h-4 w-4 text-teal-600" />
                      {t.contact.info.locationValue}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-navy-900/50">{t.contact.info.areaLabel}</dt>
                    <dd className="mt-1.5 flex items-center gap-2 font-semibold text-navy-900">
                      <Globe className="h-4 w-4 text-teal-600" />
                      {t.contact.info.areaValue}
                    </dd>
                  </div>
                </dl>
                <p className="mt-8 flex gap-2.5 rounded-2xl bg-paper-sand/70 p-4 text-sm leading-relaxed text-navy-900/70">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
                  {t.contact.info.confidentiality}
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div data-testid="contact-form-card" className="rounded-[2rem] border border-navy-900/10 bg-white/60 p-6 shadow-[0_30px_60px_-40px_rgba(12,31,56,0.3)] sm:p-10">
                {status === "success" ? (
                  <div data-testid="form-success" className="py-10 text-center" role="status">
                    <CheckCircle2 className="mx-auto h-14 w-14 text-teal-600" />
                    <h2 className="mt-5 font-display text-3xl text-navy-900">{f.success.title}</h2>
                    <p className="mx-auto mt-3 max-w-md text-navy-900/70">{f.success.body}</p>
                    <button type="button" onClick={reset} data-testid="form-success-again" className="btn-secondary mt-8">
                      {f.success.again}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                    {status === "error" && serverError && (
                      <div data-testid="form-error" role="alert" className="mb-6 flex items-start gap-2.5 rounded-xl border border-red-600/25 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        {serverError}
                      </div>
                    )}

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field id="name" label={f.name} required error={errors.name}>
                        <input id="name" type="text" value={values.name} onChange={set("name")} placeholder={f.namePh} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} required className="input" data-testid="contact-name-input" />
                      </Field>
                      <Field id="organization" label={f.organization} required error={errors.organization}>
                        <input id="organization" type="text" value={values.organization} onChange={set("organization")} placeholder={f.organizationPh} autoComplete="organization" aria-invalid={!!errors.organization} aria-describedby={errors.organization ? "organization-error" : undefined} required className="input" data-testid="contact-organization-input" />
                      </Field>
                      <Field id="email" label={f.email} required error={errors.email}>
                        <input id="email" type="email" value={values.email} onChange={set("email")} placeholder={f.emailPh} autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} required className="input" data-testid="contact-email-input" />
                      </Field>
                      <Field id="phone" label={f.phone} error={errors.phone}>
                        <input id="phone" type="tel" value={values.phone} onChange={set("phone")} placeholder={f.phonePh} autoComplete="tel" className="input" data-testid="contact-phone-input" />
                      </Field>
                      <Field id="country" label={f.country} required error={errors.country}>
                        <SelectWrap testid="contact-country-chevron">
                          <select id="country" value={values.country} onChange={set("country")} aria-invalid={!!errors.country} aria-describedby={errors.country ? "country-error" : undefined} required className="input appearance-none pr-10" data-testid="contact-country-select">
                            <option value="" disabled>{f.countryPh}</option>
                            {f.countries.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </SelectWrap>
                      </Field>
                      <Field id="website" label={f.website} error={errors.website}>
                        <input id="website" type="url" value={values.website} onChange={set("website")} placeholder={f.websitePh} autoComplete="url" className="input" data-testid="contact-website-input" />
                      </Field>
                      <Field id="locations" label={f.locations} error={errors.locations}>
                        <SelectWrap testid="contact-locations-chevron">
                          <select id="locations" value={values.locations} onChange={set("locations")} className="input appearance-none pr-10" data-testid="contact-locations-select">
                            <option value="">{f.locationsPh}</option>
                            {f.locationOptions.map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        </SelectWrap>
                      </Field>
                      <Field id="interest" label={f.interest} error={errors.interest}>
                        <SelectWrap testid="contact-interest-chevron">
                          <select id="interest" value={values.interest} onChange={set("interest")} className="input appearance-none pr-10" data-testid="contact-interest-select">
                            {f.interestOptions.map((o) => (
                              <option key={o.v} value={o.v}>{o.label}</option>
                            ))}
                          </select>
                        </SelectWrap>
                      </Field>
                    </div>

                    <div className="mt-5">
                      <Field id="message" label={f.message} required error={errors.message}>
                        <textarea id="message" rows={5} value={values.message} onChange={set("message")} placeholder={f.messagePh} aria-invalid={!!errors.message} aria-describedby={errors.message ? "message-error" : undefined} required className="input resize-y" data-testid="contact-message-textarea" />
                      </Field>
                    </div>

                    <div className="hidden" aria-hidden="true">
                      <label>
                        {f.honeypot}
                        <input type="text" tabIndex={-1} autoComplete="off" value={values.fax} onChange={set("fax")} data-testid="contact-fax-input" />
                      </label>
                    </div>

                    <label className="mt-6 flex cursor-pointer items-start gap-3" data-testid="contact-consent-label">
                      <input type="checkbox" checked={values.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 rounded border-navy-900/30 accent-teal-600" data-testid="contact-consent-checkbox" />
                      <span className="text-sm leading-relaxed text-navy-900/75">{f.consent}</span>
                    </label>

                    <p className="mt-5 text-sm leading-relaxed text-navy-900/60">
                      {f.privacyNote}{" "}
                      <Link to={`${base}/privacy`} data-testid="contact-privacy-link" className="font-semibold text-teal-700 underline underline-offset-2 transition-colors duration-200 hover:text-teal-800">
                        {f.privacyLink}
                      </Link>
                      .
                    </p>

                    <button type="submit" disabled={status === "sending"} data-testid="contact-submit-button" className="btn-primary mt-8 w-full sm:w-auto disabled:pointer-events-none disabled:opacity-60">
                      {status === "sending" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {f.sending}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          {f.submit}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
